'use client';

import { type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

type Options = {
  /** Sync or async click handler. If it returns a Promise, the button enters
   *  the pending state until the Promise resolves. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => unknown;
  /** When `true`, click events are neutralized (preventDefault + stopPropagation).
   *  Used to short-circuit clicks while disabled or already loading. The hook
   *  also self-blocks while its own returned `isPending` is `true`. */
  isBlocked: boolean;
};

/**
 * Shared click handler for `Button` and `IconButton`. Tracks Promise pending
 * state so the parent can show a loading bar while the action resolves, and
 * short-circuits clicks while the button is blocked.
 *
 * Uses `useState` + a mounted-ref instead of `useTransition` so the pending
 * tracking doesn't rely on React's transition behavior — that's documented
 * as a UI-priority primitive, not a Promise tracker, and could change.
 */
export const useAsyncClick = ({ onClick, isBlocked }: Options) => {
  const [isPending, setIsPending] = useState(false);
  const isMountedRef = useRef(true);
  const isPendingRef = useRef(false);

  useEffect(() => {
    // Reset on mount so StrictMode's mount → cleanup → mount cycle doesn't
    // leave the ref stuck at `false` (the cleanup runs between the two
    // mounts and the second mount needs to re-arm the flag).
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (isBlocked || isPendingRef.current) {
        // aria-disabled doesn't block submit, so neutralize the event ourselves.
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!onClick) {
        return;
      }
      const result = onClick(event);
      if (result instanceof Promise) {
        isPendingRef.current = true;
        setIsPending(true);
        result.finally(() => {
          isPendingRef.current = false;
          if (isMountedRef.current) {
            setIsPending(false);
          }
        });
      }
    },
    [onClick, isBlocked],
  );

  return { handleClick, isPending };
};
